/**
 * useParkingJobStatus Hook
 *
 * Provides real-time job status tracking for async domain parking operations.
 * Polls the parking job endpoint every second while the job is queued or running.
 *
 * Status states: JQ (queued), JR (running), JP (pass), JF (fail)
 */

import { useEffect, useRef } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useQuery } from '@/utils/api';

type ParkingJobState = 'JQ' | 'JR' | 'JP' | 'JF' | null;

interface ParkingJob {
  key: string;
  dns: string;
  status: string; // Format: "state#timestamp"
  comment: string;
}

// Response structure from /my endpoint
interface MyEndpointResponse {
  count: number;
  jobs: ParkingJob[];
}

interface UseParkingJobStatusOptions {
  /**
   * Domain name to track parking job for
   */
  domain: string;
  /**
   * Whether the hook is enabled (drawer is open)
   */
  enabled?: boolean;
  /**
   * Callback when job succeeds
   */
  onSuccess?: () => void;
  /**
   * Callback when job fails with error comment
   */
  onError?: (comment: string) => void;
}

interface UseParkingJobStatusResult {
  /**
   * Current job state: queued, running, pass, fail, or null if no job
   */
  jobState: ParkingJobState;
  /**
   * Whether the job is actively being processed (queued or running)
   */
  isJobActive: boolean;
  /**
   * Whether we're currently fetching job status
   */
  isLoading: boolean;
  /**
   * Error comment from failed job
   */
  errorComment: string | null;
  /**
   * Manually trigger a refetch
   */
  refetch: () => void;
}

/**
 * Extract job state from status string.
 * Status format: "state#timestamp" (e.g., "JR#2025-12-17T07:04:28Z")
 * States: JQ (queued), JR (running), JP (pass), JF (fail)
 */
function extractJobState(status: string | undefined): ParkingJobState {
  if (!status) return null;
  const state = status.split('#')[0]; // Get state part before timestamp
  if (['JQ', 'JR', 'JP', 'JF'].includes(state)) {
    return state as ParkingJobState;
  }
  return null;
}

/**
 * Extract timestamp from status string.
 * Status format: "state#timestamp" (e.g., "JR#2025-12-17T07:04:28Z")
 */
function extractJobTimestamp(status: string | undefined): string | null {
  if (!status) return null;
  const parts = status.split('#');
  return parts.length > 1 ? parts[1] : null;
}

export function useParkingJobStatus({
  domain,
  enabled = true,
  onSuccess,
  onError,
}: UseParkingJobStatusOptions): UseParkingJobStatusResult {
  const axios = useAxios();

  // Track previous state to detect transitions for callbacks
  const prevStateRef = useRef<ParkingJobState>(null);
  // Track the job timestamp to detect when a new job starts
  const prevTimestampRef = useRef<string | null>(null);
  // Track whether we've already fired a callback for the current job
  const callbackFiredRef = useRef<boolean>(false);
  // Track whether a new job is awaiting completion callback
  // (set when we detect a new timestamp, cleared when callback fires)
  const newJobAwaitingCallbackRef = useRef<boolean>(false);

  const {
    data: job,
    status: queryStatus,
    refetch,
  } = useQuery({
    queryKey: ['parking-job', domain],
    queryFn: async () => {
      if (!domain) return null;

      try {
        // Construct job key for parking operations: #job#{domain}#system#parking
        const jobKey = `#job#${domain}#system#parking`;
        const response = await axios.get<MyEndpointResponse>(
          `/my?key=${encodeURIComponent(jobKey)}&red_team=true&exact=true`
        );

        // Extract first job from jobs array (expect exactly one match or zero)
        if (
          response.data.count === 0 ||
          !response.data.jobs ||
          response.data.jobs.length === 0
        ) {
          return null;
        }

        return response.data.jobs[0];
      } catch (error: unknown) {
        // 404 means no job exists - treat as null, not error
        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { status?: number } }).response?.status === 404
        ) {
          return null;
        }
        throw error;
      }
    },
    defaultErrorMessage: 'Failed to fetch parking job status',
    enabled: enabled && Boolean(domain),
    // Poll every 1 second when job is queued or running
    refetchInterval: query => {
      const jobData = query.state.data as ParkingJob | null | undefined;
      if (!jobData) return false;
      const state = extractJobState(jobData.status);
      // Continue polling while job is active (JQ or JR)
      return state === 'JQ' || state === 'JR' ? 1000 : false;
    },
    // Don't refetch on window focus during active polling
    refetchOnWindowFocus: false,
    // Short stale time since we're polling
    staleTime: 500,
  });

  const jobState = extractJobState(job?.status);
  const isJobActive = jobState === 'JQ' || jobState === 'JR';
  const errorComment = jobState === 'JF' ? job?.comment || null : null;

  // Force refetch when domain becomes available
  useEffect(() => {
    if (domain && enabled) {
      refetch();
    }
  }, [domain, enabled, refetch]);

  // Handle state transitions for callbacks
  useEffect(() => {
    const prevState = prevStateRef.current;
    const currentTimestamp = extractJobTimestamp(job?.status);

    // Detect if this is a new job by comparing timestamps
    const isNewJob =
      currentTimestamp !== null &&
      prevTimestampRef.current !== null &&
      currentTimestamp !== prevTimestampRef.current;

    // Reset tracking when a new job starts
    if (isNewJob) {
      prevStateRef.current = null;
      callbackFiredRef.current = false;
      // Mark new job as awaiting callback - we want to fire when it completes
      newJobAwaitingCallbackRef.current = true;
    }

    // First job after page load - don't mark as awaiting callback
    // (we don't want to fire for old jobs that completed before we started watching)
    if (currentTimestamp !== null && prevTimestampRef.current === null) {
      callbackFiredRef.current = false;
      newJobAwaitingCallbackRef.current = false;
    }

    // Only fire callbacks once per job (prevent duplicate toasts)
    const shouldFireCallback = !callbackFiredRef.current;

    // Determine if we should fire callback:
    // 1. Normal transition: previous state was JQ/JR, current is JP/JF
    // 2. New job completed: timestamp changed and job reached terminal state
    //    (handles case where polling missed JQ/JR states)
    const wasActiveInCurrentJob = prevState === 'JQ' || prevState === 'JR';
    const isTerminalState = jobState === 'JP' || jobState === 'JF';
    const shouldFireForNewJob =
      newJobAwaitingCallbackRef.current && isTerminalState;

    // Call onSuccess when job passes
    if (
      shouldFireCallback &&
      jobState === 'JP' &&
      (wasActiveInCurrentJob || shouldFireForNewJob)
    ) {
      callbackFiredRef.current = true;
      newJobAwaitingCallbackRef.current = false;
      onSuccess?.();
    }

    // Call onError when job fails
    if (
      shouldFireCallback &&
      jobState === 'JF' &&
      (wasActiveInCurrentJob || shouldFireForNewJob)
    ) {
      callbackFiredRef.current = true;
      newJobAwaitingCallbackRef.current = false;
      onError?.(job?.comment || 'Parking operation failed');
    }

    prevStateRef.current = jobState;
    prevTimestampRef.current = currentTimestamp;
  }, [jobState, job?.status, job?.comment, onSuccess, onError]);

  return {
    jobState,
    isJobActive,
    isLoading: queryStatus === 'pending',
    errorComment,
    refetch,
  };
}
